import React        from "react";
import styled       from 'styled-components';
import Body         from "../../../../component/body";
import { pageType } from "../../../../pages";
import * as utils   from "../../../../utils";

// ── Styled components ────────────────────────────────────────────────────────

const StyledContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: center;
    align-self: center;
    min-width: 24rem;
    max-width: 48rem;
    width: 100%;
`;

const StyledContainerContents = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-self: center;
    align-self: center;
    padding: 1rem 4rem;
    border-radius: .3rem;
    width: 100%;
    background-color: var(--color-background-box-overlay);
`;

const StyledTitle = styled.h1`
    margin: 0 0 1.5rem 0;
    font-size: 1.5em;
`;

const StyledSubTitle = styled.p`
    margin: 0 0 1rem 0;
    color: var(--color-text);
    opacity: .75;
    font-size: .9rem;
`;

const StyledFormRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: flex-end;
    flex-wrap: wrap;
`;

const StyledFieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 10rem;
`;

const StyledLabel = styled.label`
    font-size: .85rem;
    margin-bottom: .35rem;
    color: var(--color-text);
    opacity: .8;
`;

const StyledInput = styled.input`
    display: flex;
    flex-grow: 1;
    border-radius: .25rem;
    background-image: none;
    border: 0;
    color: var(--color-text);
    font: inherit;
    margin: 0;
    outline: 0;
    padding: .75rem 1rem;
    background-color: var(--color-background-input);
    transition: background-color .3s;

    &:is(:hover, :focus) {
        background-color: var(--color-background-input-hover);
        color: var(--color-text-invert-hover);
    }

    &:disabled {
        cursor: not-allowed;
        color: var(--color-text-error);
        background-color: var(--color-background-input-danger);
    }
`;

const StyledButton = styled.button`
    border-radius: .25rem;
    cursor: pointer;
    border: 0;
    outline: 0;
    padding: .75rem 1.5rem;
    background-color: var(--color-background-input-success);
    color: var(--color-text-success);
    font: inherit;
    font-weight: 600;
    transition: background-color .3s, color .3s;

    &:hover {
        color: var(--color-text-success-hover);
        background-color: var(--color-background-input-success-hover);
    }

    &:disabled {
        cursor: not-allowed;
        background-color: var(--color-background-input-danger);
        color: var(--color-text-error);
    }
`;

const StyledStatus = styled.p<{ $error?: boolean }>`
    margin: 0 0 .5rem 0;
    font-size: .9rem;
    color: ${({ $error }) => $error ? `var(--color-text-error)` : `var(--color-text-success)`};
`;

// ── Component ────────────────────────────────────────────────────────────────

type ExportState = {
    start      : string;
    stop       : string;
    isLoading  : boolean;
    status     : string;
    isError    : boolean;
};

class Page extends React.Component<{}, ExportState> {
    constructor(props: {}) {
        super(props);

        const today     = new Date();
        const todayStr  = utils.formattedDate(today, `Y-m-d`);
        const monthAgo  = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        const monthAgoStr = utils.formattedDate(monthAgo, `Y-m-d`);

        this.state = {
            start     : monthAgoStr,
            stop      : todayStr,
            isLoading : false,
            status    : ``,
            isError   : false,
        };
    }

    componentDidMount() {
        utils.ws.listener.message(
            [`account.home.usage.export`],
            (d) => {
                if (d.code === 200) {
                    const { csv, rows, start, stop } = d.results as {
                        csv  : string;
                        rows : number;
                        start: string;
                        stop : string;
                    };

                    // Trigger a file download of the CSV
                    const blob = new Blob([csv], { type: `text/csv` });
                    const url  = URL.createObjectURL(blob);
                    const a    = document.createElement(`a`);
                    a.href     = url;
                    a.download = `energie-export_${start}_${stop}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);

                    this.setState({
                        isLoading : false,
                        status    : `${rows} rijen geëxporteerd (${start} t/m ${stop}).`,
                        isError   : false,
                    });
                } else {
                    const msg = Array.isArray(d.messages) && d.messages.length > 0
                        ? d.messages.join(`, `)
                        : `Export mislukt (code ${d.code}).`;
                    this.setState({
                        isLoading : false,
                        status    : msg,
                        isError   : true,
                    });
                }
            }
        );
    }

    handleExport = () => {
        const { start, stop } = this.state;

        if (!start || !stop) {
            this.setState({ status: `Vul een begin- en einddatum in.`, isError: true });
            return;
        }

        if (new Date(start) > new Date(stop)) {
            this.setState({ status: `Begindatum mag niet na de einddatum liggen.`, isError: true });
            return;
        }

        this.setState({ isLoading: true, status: ``, isError: false }, () => {
            utils.ws.sent(`account`, `home.usage.export`, { start, stop });
        });
    };

    render() {
        const { start, stop, isLoading, status, isError } = this.state;

        return <Body>
            <StyledContainer>
                <StyledContainerContents>
                    <StyledTitle>Energie-export</StyledTitle>
                    <StyledSubTitle>
                        Download je P1-metingen als CSV-bestand. Kies een periode en klik op Exporteren.
                        De kolommen zijn: <em>timestamp, grid_power_w, tariff</em>.
                    </StyledSubTitle>

                    <StyledFormRow>
                        <StyledFieldGroup>
                            <StyledLabel htmlFor="export-start">Begindatum</StyledLabel>
                            <StyledInput
                                id="export-start"
                                type="date"
                                value={start}
                                disabled={isLoading}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ start: e.target.value })}
                            />
                        </StyledFieldGroup>

                        <StyledFieldGroup>
                            <StyledLabel htmlFor="export-stop">Einddatum</StyledLabel>
                            <StyledInput
                                id="export-stop"
                                type="date"
                                value={stop}
                                disabled={isLoading}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ stop: e.target.value })}
                            />
                        </StyledFieldGroup>

                        <StyledButton
                            onClick={this.handleExport}
                            disabled={isLoading}
                        >
                            {isLoading ? `Bezig…` : `Exporteren`}
                        </StyledButton>
                    </StyledFormRow>

                    {status.length > 0 && (
                        <StyledStatus $error={isError}>{status}</StyledStatus>
                    )}
                </StyledContainerContents>
            </StyledContainer>
        </Body>;
    }
}

// ── Page config ──────────────────────────────────────────────────────────────

const page: pageType = {
    name  : `home usage export`,
    link  : [`/home/usage/export`],
    page  : <Page />,
    props : {
        enabled  : true,
        menu     : true,
        login    : false,
    },
};

export default page;
